import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import {
  AlertTriangle,
  CheckCircle2,
  Database,
  FileArchive,
  FileWarning,
  GitPullRequest,
  Layers3,
  Loader2,
  MapPinned,
  Plus,
  RefreshCw,
  ShieldCheck,
  UploadCloud,
  XCircle,
} from 'lucide-react';
import {
  acknowledgeCoverageConflict,
  approveCoveragePublishRequest,
  coverageReasonFa,
  createCoveragePublishRequest,
  createCoverageRegion,
  createCoverageSource,
  getCoverageConflict,
  getCoverageImport,
  getCoverageImportReport,
  getCoverageVersion,
  isAsciiAuditReason,
  listCoverageConflicts,
  listCoverageImports,
  listCoveragePolicies,
  listCoveragePublishRequests,
  listCoverageRegions,
  listCoverageSources,
  listCoverageVersions,
  rejectCoveragePublishRequest,
  resolveCoverageConflict,
  updateCoverageSource,
  uploadManualCoverage,
} from '../../../lib/coverageAdminApi';
import PANEL_CAPABILITIES from '../../../shared/panelCapabilities';
import { toFa } from '../../../utils/faFormat';
import EntityPicker from './EntityPicker';
import GeometryIntake from './GeometryIntake';
import RetirementAction from './RetirementAction';
import {
  createCoverageRegistryService,
  listCoverageRegistryServices,
  listIntakeCarriers,
  listIntakeServices,
  retireCoverageImport,
  retireCoverageRegion,
  retireCoverageRegistryService,
  updateCoverageRegion,
} from '../../../lib/coverageIntakeApi';
import '../../../styles/CoverageAdminTab.css';
import '../../../styles/CoverageIntake.css';

const STEPS = [
  { id: 'setup', label: 'محدوده و منبع', icon: MapPinned },
  { id: 'imports', label: 'بارگذاری و گزارش', icon: UploadCloud },
  { id: 'conflicts', label: 'تعارض‌ها', icon: AlertTriangle },
  { id: 'publication', label: 'انتشار', icon: GitPullRequest },
  { id: 'versions', label: 'نسخه‌ها', icon: Layers3 },
];

const STATUS_FA = {
  received: 'دریافت‌شده', parsing: 'در حال خواندن', staged: 'مرحله‌بندی‌شده',
  validating: 'در حال اعتبارسنجی', ready: 'آمادهٔ انتشار', quarantined: 'قرنطینه',
  superseded: 'کنارگذاشته‌شده', published: 'منتشرشده', duplicate: 'تکراری',
  open: 'باز', acknowledged: 'دیده‌شده', resolved: 'حل‌شده',
  pending: 'در انتظار', accepted: 'پذیرفته', rejected: 'ردشده', active: 'فعال',
  retired: 'بازنشسته', executed: 'اجراشده', failed: 'ناموفق', draft: 'پیش‌نویس',
  revoked: 'لغوشده', superseded_version: 'جایگزین‌شده',
  publish_scope: 'انتشار محدوده', revoke_scope: 'لغو محدوده', rollback_version: 'بازگشت نسخه',
};

export const ERROR_MESSAGES = {
  MANUAL_SOURCE_NOT_AVAILABLE: 'این منبع برای بارگذاری دستی آماده نیست؛ منبع باید پذیرفته و بازنشسته‌نشده باشد.',
  OPERATOR_API_PRECEDENCE: 'برای این محدوده یک منبع API پذیرفته‌شده وجود دارد و طبق قاعده، منبع دستی نمی‌تواند جای آن را بگیرد.',
  MANUAL_IMPORT_NOT_READY: 'فقط بارگذاری آماده از یک منبع دستی پذیرفته‌شده قابل پیشنهاد برای انتشار است.',
  POLICY_REVISION_REQUIRED: 'انتشار به یک خط‌مشی فعال نیاز دارد.',
  PUBLISH_PREVIEW_STALE: 'نسخه یا فایل نامزد پس از پیش‌نمایش تغییر کرده است؛ صفحه را تازه‌سازی و دوباره بررسی کنید.',
  REQUEST_ID_REQUIRED: 'شناسهٔ حسابرسی درخواست ساخته نشد؛ دوباره تلاش کنید.',
  AUDIT_REASON_REQUIRED: 'دلیل اقدام را وارد کنید.',
  INVALID_REGION_GEOMETRY: 'GeoJSON منطقه باید یک MultiPolygon معتبر و غیرخالی با SRID 4326 باشد.',
  CARRIER_SERVICE_NOT_ACTIVE: 'این سرویس برای اپراتور انتخاب‌شده فعال نیست؛ فهرست سرویس‌ها را تازه‌سازی کنید.',
  SERVICE_TYPE_TAKEN: 'این کد نوع سرویس پیش‌تر برای همین اپراتور در رجیستری پوشش ثبت شده است؛ کد دیگری انتخاب کنید.',
  INVALID_SERVICE_TYPE: 'کد نوع سرویس باید با حرف کوچک انگلیسی آغاز شود و فقط از حروف کوچک، عدد، خط تیره یا زیرخط ساخته شود؛ نمونهٔ معتبر: fiber یا fixed_fiber.',
  SERVICE_TYPE_IMMUTABLE: 'کد نوع سرویس پس از ساخت قابل تغییر نیست؛ برای کد جدید سرویس تازه‌ای بسازید.',
  SERVICE_IN_USE: 'این سرویس در یک منبع فعال استفاده شده است. ابتدا منبع را بازنشسته کنید.',
  OPERATOR_RETIRED: 'این اپراتور بازنشسته است و نمی‌توان سرویس تازه‌ای به رجیستری پوشش آن افزود.',
  REGION_IN_USE: 'این محدوده در یک منبع فعال استفاده شده است. ابتدا منبع را بازنشسته کنید.',
  REGION_GEOMETRY_LOCKED: 'مرز این محدوده در یک محدودهٔ پوشش فعال استفاده شده است. ابتدا آن محدودهٔ پوشش را بازنشسته کنید.',
  IMPORT_ALREADY_RETIRED: 'این بارگذاری پیش‌تر بازنشسته شده است و اقدام دوباره‌ای انجام نشد.',
};

const SERVICE_TYPE_PATTERN = /^[a-z][a-z0-9]*(?:[-_][a-z0-9]+)*$/;
const SERVICE_TYPE_SUGGESTIONS = ['fiber', 'adsl', 'tdlte', 'mobile'];
const TERMINAL_IMPORT_STATUSES = new Set(['ready', 'quarantined', 'superseded', 'published', 'duplicate', 'retired']);
const EMPTY_REGION = { region_code: '', display_name: '', geometry: '', reason: '' };
const EMPTY_REGION_EDIT = { display_name: '', geometry: '', reason: '' };
const EMPTY_SOURCE = {
  carrier_id: '', service_type: '', region_id: '', source_name: '', reason: '',
};
const EMPTY_SERVICE = { display_name: '', service_type: '', reason: '' };

function requestId() {
  return window.crypto.randomUUID();
}

function audit(reason) {
  return { requestId: requestId(), reason: reason.trim() };
}

function formatDate(value) {
  if (!value) return '-';
  const date = new Date(value);
  if (Number.isNaN(date.getTime())) return '-';
  return toFa(new Intl.DateTimeFormat('fa-IR', {
    year: 'numeric', month: '2-digit', day: '2-digit', hour: '2-digit', minute: '2-digit',
  }).format(date));
}

function formatNumber(value, options) {
  const number = Number(value);
  if (!Number.isFinite(number)) return '-';
  return toFa(new Intl.NumberFormat('fa-IR', options).format(number));
}

function formatBytes(value) {
  const bytes = Number(value);
  if (!Number.isFinite(bytes)) return '-';
  if (bytes < 1024) return `${toFa(bytes)} بایت`;
  if (bytes < 1024 ** 2) return `${formatNumber(bytes / 1024, { maximumFractionDigits: 1 })} کیلوبایت`;
  return `${formatNumber(bytes / (1024 ** 2), { maximumFractionDigits: 1 })} مگابایت`;
}

function statusFa(status) {
  return STATUS_FA[status] || status || 'نامشخص';
}

function isRetired(item) {
  return item?.status === 'retired' || Boolean(item?.retired_at);
}

function apiError(result) {
  if (!result?.error) return '';
  if (result.code === 'NETWORK_ERROR') return result.error;
  if (result.code === 'REGION_IN_USE' && Number.isFinite(Number(result.details?.sources))) {
    return `این محدوده در ${formatNumber(result.details.sources)} منبع فعال استفاده شده است. ابتدا منبع‌ها را بازنشسته کنید.`;
  }
  if (result.code === 'SERVICE_IN_USE' && Number.isFinite(Number(result.details?.sources))) {
    return `این سرویس در ${formatNumber(result.details.sources)} منبع فعال استفاده شده است. ابتدا منبع‌ها را بازنشسته کنید.`;
  }
  if (result.code === 'REGION_GEOMETRY_LOCKED' && Number.isFinite(Number(result.details?.areas))) {
    return `مرز این محدوده در ${formatNumber(result.details.areas)} محدودهٔ پوشش فعال استفاده شده است. ابتدا آن محدوده‌های پوشش را بازنشسته کنید.`;
  }
  return ERROR_MESSAGES[result.code] || coverageReasonFa(result);
}

function stableReasonValue(value) {
  if (Array.isArray(value)) return `[${value.map(stableReasonValue).join(',')}]`;
  if (value && typeof value === 'object') {
    return `{${Object.keys(value).sort().map((key) => `${JSON.stringify(key)}:${stableReasonValue(value[key])}`).join(',')}}`;
  }
  return JSON.stringify(value ?? null);
}

function dedupeReasons(reasons) {
  const seen = new Set();
  return reasons.filter((reason) => {
    const key = stableReasonValue([
      reason.code || reason.reason_code,
      reason.feature_ordinal,
      reason.external_feature_id,
      reason.reason_detail || reason.message || reason.error,
      reason.evidence,
    ]);
    if (seen.has(key)) return false;
    seen.add(key);
    return true;
  });
}

function StatusBadge({ status, current }) {
  return (
    <span className={`cov-status cov-status-${status || 'unknown'}`}>
      {current && <span className="cov-live-dot" aria-hidden="true" />}
      {current ? 'نسخهٔ جاری' : statusFa(status)}
    </span>
  );
}

function EmptyState({ icon: Icon = Database, children }) {
  return (
    <div className="cov-empty">
      <Icon size={24} aria-hidden="true" />
      <p>{children}</p>
    </div>
  );
}

function PanelTitle({ title, description, count }) {
  return (
    <div className="cov-panel-title">
      <div>
        <h3>{title}</h3>
        {description && <p>{description}</p>}
      </div>
      {count !== undefined && <span className="cov-count">{toFa(count)}</span>}
    </div>
  );
}

export default function CoverageAdminTab({ role }) {
  const visibleSteps = STEPS.filter(({ id }) => (
    PANEL_CAPABILITIES.canAccessPanelSection(role, `coverage.${id}`)
  ));
  const visibleStepIds = new Set(visibleSteps.map(({ id }) => id));
  const canManageSetup = role === 'super_admin' || role === 'admin';
  const canManageCatalogue = PANEL_CAPABILITIES.canAccessPanelSection(role, 'catalogue');
  const canPublish = canManageSetup;
  const [activeStep, setActiveStep] = useState(visibleSteps[0]?.id || '');
  const selectedStep = visibleStepIds.has(activeStep) ? activeStep : (visibleSteps[0]?.id || '');
  const [regions, setRegions] = useState([]);
  const [sourceServiceCount, setSourceServiceCount] = useState(null);
  const [sourceServices, setSourceServices] = useState([]);
  const [serviceReloadKey, setServiceReloadKey] = useState(0);
  const [sources, setSources] = useState([]);
  const [imports, setImports] = useState([]);
  const [conflicts, setConflicts] = useState([]);
  const [publishRequests, setPublishRequests] = useState([]);
  const [versions, setVersions] = useState([]);
  const [policies, setPolicies] = useState([]);
  const [loading, setLoading] = useState(true);
  const [busy, setBusy] = useState('');
  const [error, setError] = useState('');
  const [notice, setNotice] = useState('');

  const [regionForm, setRegionForm] = useState(EMPTY_REGION);
  const [regionActionId, setRegionActionId] = useState('');
  const [regionEditForm, setRegionEditForm] = useState(EMPTY_REGION_EDIT);
  const [sourceForm, setSourceForm] = useState(EMPTY_SOURCE);
  const [sourceActionId, setSourceActionId] = useState('');
  const [serviceActionId, setServiceActionId] = useState('');
  const [showServiceForm, setShowServiceForm] = useState(false);
  const [serviceForm, setServiceForm] = useState(EMPTY_SERVICE);
  const [serviceFormError, setServiceFormError] = useState('');
  const [uploadSourceId, setUploadSourceId] = useState('');
  const [uploadReason, setUploadReason] = useState('');
  const [uploadFile, setUploadFile] = useState(null);
  const [uploadProgress, setUploadProgress] = useState(0);
  const [pollingImportId, setPollingImportId] = useState('');
  const fileInputRef = useRef(null);
  const retiredCacheRef = useRef({
    regions: new Map(), sources: new Map(), imports: new Map(), services: new Map(),
  });

  const importRequestRef = useRef(0);
  const [selectedImportId, setSelectedImportId] = useState('');
  const [selectedImport, setSelectedImport] = useState(null);
  const [selectedReport, setSelectedReport] = useState(null);
  const [conflictNotes, setConflictNotes] = useState({});
  const [selectedConflict, setSelectedConflict] = useState(null);
  const [publishForm, setPublishForm] = useState({ import_id: '', policy_id: '', reason: '' });
  const [decisionReasons, setDecisionReasons] = useState({});
  const [selectedVersion, setSelectedVersion] = useState(null);

  const mergeCachedRetired = useCallback((kind, items = []) => {
    const cache = retiredCacheRef.current[kind];
    const merged = new Map(items.map((item) => [item.id, cache.get(item.id) || item]));
    cache.forEach((item, id) => {
      if (!merged.has(id)) merged.set(id, item);
    });
    return [...merged.values()];
  }, []);

  const rememberRetired = (kind, original, returned, setter) => {
    const retired = {
      ...original,
      ...(returned || {}),
      status: 'retired',
      retired_at: returned?.retired_at || original?.retired_at || new Date().toISOString(),
    };
    retiredCacheRef.current[kind].set(retired.id, retired);
    setter((current) => current.map((item) => (item.id === retired.id ? retired : item)));
    return retired;
  };

  const activeRegions = useMemo(() => regions.filter((region) => !isRetired(region)), [regions]);
  const acceptedSources = useMemo(
    () => sources.filter((source) => ['manual', 'manual_file'].includes(source.source_type)
      && source.acceptance_status === 'accepted' && !source.retired_at),
    [sources],
  );
  const readyImports = useMemo(
    () => imports.filter((entry) => entry.status === 'ready' && !isRetired(entry)),
    [imports],
  );
  const activePolicies = useMemo(
    () => policies.filter((policy) => policy.status === 'active'),
    [policies],
  );

  const refreshAll = useCallback(async ({ quiet = false } = {}) => {
    if (!quiet) setLoading(true);
    const results = await Promise.all([
      listCoverageRegions(), listCoverageSources(), listCoverageImports({ limit: 100 }),
      listCoverageConflicts({ limit: 100 }), listCoveragePublishRequests({ limit: 100 }),
      listCoverageVersions({ limit: 100 }), listCoveragePolicies(),
    ]);
    const firstError = results.find((result) => result.error);
    if (firstError) setError(apiError(firstError));
    else setError('');
    setRegions(mergeCachedRetired('regions', results[0].regions || []));
    setSources(mergeCachedRetired('sources', results[1].sources || []));
    setImports(mergeCachedRetired('imports', results[2].imports || []));
    setConflicts(results[3].conflicts || []);
    setPublishRequests(results[4].publish_requests || []);
    setVersions(results[5].versions || []);
    setPolicies(results[6].policies || []);
    setLoading(false);
  }, [mergeCachedRetired]);

  useEffect(() => {
    if (visibleSteps.length) refreshAll();
    else setLoading(false);
  }, [refreshAll, visibleSteps.length]);

  useEffect(() => {
    if (!uploadSourceId && acceptedSources.length) setUploadSourceId(acceptedSources[0].id);
  }, [acceptedSources, uploadSourceId]);

  useEffect(() => {
    const selectedRegionIsActive = activeRegions.some((region) => region.id === sourceForm.region_id);
    if ((!sourceForm.region_id || !selectedRegionIsActive) && activeRegions.length) {
      setSourceForm((current) => ({ ...current, region_id: activeRegions[0].id }));
    } else if (!activeRegions.length && sourceForm.region_id) {
      setSourceForm((current) => ({ ...current, region_id: '' }));
    }
  }, [activeRegions, sourceForm.region_id]);

  const loadSourceServices = async (signal) => {
    if (!sourceForm.carrier_id) return { services: [] };
    const loader = typeof listCoverageRegistryServices === 'function'
      ? listCoverageRegistryServices
      : listIntakeServices;
    const result = await loader(sourceForm.carrier_id, signal);
    const normalizedServices = (result.services || []).map((service) => ({
      ...service,
      id: service.id || service.service_type,
    }));
    const registryServices = mergeCachedRetired('services', normalizedServices);
    const activeServices = registryServices.filter((service) => !isRetired(service));
    setSourceServices(registryServices);
    setSourceServiceCount(activeServices.length);
    return {
      services: activeServices.map((service) => ({
        ...service,
        registry_id: service.id,
        id: service.service_type,
      })),
    };
  };

  const loadImport = useCallback(async (id) => {
    const requestSequence = ++importRequestRef.current;
    setSelectedImportId(id);
    setSelectedImport(null);
    setSelectedReport(null);
    const [detail, report] = await Promise.all([getCoverageImport(id), getCoverageImportReport(id)]);
    if (requestSequence !== importRequestRef.current) return;
    if (detail.error || report.error) {
      setError(apiError(detail.error ? detail : report));
      return;
    }
    setSelectedImport(detail);
    setSelectedReport(report);
  }, []);

  useEffect(() => {
    if (!selectedImportId && imports.length) loadImport(imports[0].id);
  }, [imports, loadImport, selectedImportId]);

  useEffect(() => {
    if (!pollingImportId) return undefined;
    let alive = true;
    const poll = async () => {
      const report = await getCoverageImportReport(pollingImportId);
      if (!alive) return;
      if (report.error) {
        setPollingImportId('');
        setError(apiError(report));
        setNotice('');
        return;
      }
      setSelectedReport(report);
      if (TERMINAL_IMPORT_STATUSES.has(report.status)) {
        setPollingImportId('');
        setNotice(report.status === 'ready'
          ? 'اعتبارسنجی تمام شد و فایل برای درخواست انتشار آماده است.'
          : 'اعتبارسنجی تمام شد؛ جزئیات قرنطینه یا تقدم منبع را در گزارش ببینید.');
        await loadImport(pollingImportId);
        await refreshAll({ quiet: true });
      }
    };
    poll();
    const timer = window.setInterval(poll, 1500);
    return () => { alive = false; window.clearInterval(timer); };
  }, [loadImport, pollingImportId, refreshAll]);

  const setResult = (result, success) => {
    if (result.error) {
      setError(apiError(result));
      setNotice('');
      return false;
    }
    setError('');
    setNotice(success);
    return true;
  };

  const submitRegion = async (event) => {
    event.preventDefault();
    let geometry;
    try { geometry = JSON.parse(regionForm.geometry); } catch {
      setError('GeoJSON منطقه معتبر نیست؛ JSON را بررسی کنید.');
      return;
    }
    setBusy('region');
    const result = await createCoverageRegion({
      region_code: regionForm.region_code.trim(),
      display_name: regionForm.display_name.trim(),
      geometry,
      metadata: {},
    }, audit(regionForm.reason));
    setBusy('');
    if (!setResult(result, 'محدوده ساخته شد. اکنون منبع دستی آن را تعریف کنید.')) return;
    setRegionForm(EMPTY_REGION);
    await refreshAll({ quiet: true });
  };

  const openRegionActions = (region) => {
    if (regionActionId === region.id) {
      setRegionActionId('');
      setRegionEditForm(EMPTY_REGION_EDIT);
      return;
    }
    setRegionActionId(region.id);
    setRegionEditForm({ display_name: region.display_name || '', geometry: '', reason: '' });
  };

  const submitRegionEdit = async (event, region) => {
    event.preventDefault();
    const geometryText = regionEditForm.geometry.trim();
    let geometry;
    if (geometryText) {
      try { geometry = JSON.parse(geometryText); } catch {
        setError('GeoJSON اصلاح مرز معتبر نیست؛ JSON را بررسی کنید.');
        return;
      }
    }
    setBusy(`region-edit-${region.id}`);
    const result = await updateCoverageRegion(region.id, {
      display_name: regionEditForm.display_name.trim(),
      ...(geometryText ? { geometry } : {}),
    }, audit(regionEditForm.reason));
    setBusy('');
    if (!setResult(result, geometryText
      ? 'نام و مرز محدوده اصلاح شد و مرز تازه از انتشار بعدی در مجموعهٔ کاری اثر می‌گذارد.'
      : 'نام محدوده اصلاح شد.')) return;
    if (!result.region?.id) {
      setError('پاسخ اصلاح محدوده کامل نبود؛ صفحه را تازه‌سازی و نتیجه را بررسی کنید.');
      setNotice('');
      return;
    }
    setRegions((current) => current.map((item) => (item.id === region.id ? { ...item, ...result.region } : item)));
    setRegionActionId('');
    setRegionEditForm(EMPTY_REGION_EDIT);
  };

  const retireRegion = async (region, reason) => {
    setBusy(`region-retire-${region.id}`);
    const result = await retireCoverageRegion(region.id, audit(reason));
    setBusy('');
    if (!setResult(result, 'محدوده بازنشسته شد و از انتشار بعدی کنار گذاشته شد.')) return false;
    if (!result.region?.id) {
      setError('پاسخ بازنشستگی محدوده کامل نبود؛ صفحه را تازه‌سازی و نتیجه را بررسی کنید.');
      setNotice('');
      return false;
    }
    rememberRetired('regions', region, result.region, setRegions);
    setRegionActionId('');
    return true;
  };

  const submitSource = async (event) => {
    event.preventDefault();
    setBusy('source');
    const result = await createCoverageSource({
      carrier_id: sourceForm.carrier_id.trim(),
      service_type: sourceForm.service_type.trim(),
      region_id: sourceForm.region_id,
      source_type: 'manual_file',
      source_name: sourceForm.source_name.trim(),
      acceptance_status: 'accepted',
      sync_config: {},
    }, audit(sourceForm.reason));
    setBusy('');
    if (!setResult(result, 'منبع دستی پذیرفته شد و برای بارگذاری آماده است.')) return;
    setSourceForm((current) => ({ ...EMPTY_SOURCE, region_id: current.region_id }));
    await refreshAll({ quiet: true });
  };

  const acceptSource = async (source) => {
    if (!sourceForm.reason.trim()) {
      setError('برای پذیرش منبع، دلیل حسابرسی را در فرم منبع وارد کنید.');
      return;
    }
    setBusy(`source-${source.id}`);
    const result = await updateCoverageSource(source.id, { acceptance_status: 'accepted' }, audit(sourceForm.reason));
    setBusy('');
    if (!setResult(result, 'منبع پذیرفته شد و اکنون در فهرست بارگذاری دیده می‌شود.')) return;
    await refreshAll({ quiet: true });
  };

  const retireSource = async (source, reason) => {
    setBusy(`source-retire-${source.id}`);
    const result = await updateCoverageSource(source.id, { retire: true }, audit(reason));
    setBusy('');
    const retiredAreas = Number(result.retired_areas);
    const success = Number.isFinite(retiredAreas)
      ? `منبع بازنشسته شد و ${formatNumber(retiredAreas)} محدودهٔ پوشش آن از انتشار بعدی کنار گذاشته شد.`
      : 'منبع بازنشسته شد و محدوده‌های پوشش آن از انتشار بعدی کنار گذاشته شدند.';
    if (!setResult(result, success)) return false;
    if (!result.source?.id) {
      setError('پاسخ بازنشستگی منبع کامل نبود؛ صفحه را تازه‌سازی و نتیجه را بررسی کنید.');
      setNotice('');
      return false;
    }
    rememberRetired('sources', source, result.source, setSources);
    if (uploadSourceId === source.id) setUploadSourceId('');
    setSourceActionId('');
    return true;
  };

  const submitRegistryService = async (event) => {
    event.preventDefault();
    const serviceType = serviceForm.service_type.trim();
    if (!SERVICE_TYPE_PATTERN.test(serviceType)) {
      setServiceFormError(ERROR_MESSAGES.INVALID_SERVICE_TYPE);
      return;
    }
    setServiceFormError('');
    setBusy('service-create');
    const result = await createCoverageRegistryService(sourceForm.carrier_id, {
      display_name: serviceForm.display_name.trim(),
      service_type: serviceType,
    }, audit(serviceForm.reason));
    setBusy('');
    if (!setResult(result, `سرویس «${serviceForm.display_name.trim()}» به رجیستری پوشش اضافه و در انتخاب‌گر انتخاب شد.`)) return;
    if (!result.service?.id || !result.service?.service_type) {
      setError('پاسخ ساخت سرویس کامل نبود؛ صفحه را تازه‌سازی و نتیجه را بررسی کنید.');
      setNotice('');
      return;
    }
    setSourceForm((current) => ({ ...current, service_type: result.service.service_type }));
    setServiceForm(EMPTY_SERVICE);
    setShowServiceForm(false);
    setServiceReloadKey((current) => current + 1);
  };

  const retireRegistryService = async (service, reason) => {
    setBusy(`service-retire-${service.id}`);
    const result = await retireCoverageRegistryService(sourceForm.carrier_id, service.id, audit(reason));
    setBusy('');
    if (!setResult(result, 'سرویس رجیستری پوشش بازنشسته شد و از انتشار بعدی کنار گذاشته شد.')) return false;
    if (!result.service?.id) {
      setError('پاسخ بازنشستگی سرویس کامل نبود؛ صفحه را تازه‌سازی و نتیجه را بررسی کنید.');
      setNotice('');
      return false;
    }
    rememberRetired('services', service, result.service, setSourceServices);
    if (sourceForm.service_type === service.service_type) {
      setSourceForm((current) => ({ ...current, service_type: '' }));
    }
    setServiceActionId('');
    setServiceReloadKey((current) => current + 1);
    return true;
  };

  const submitUpload = async (event) => {
    event.preventDefault();
    const reason = uploadReason.trim();
    if (!uploadFile || !uploadSourceId || !reason) return;
    if (!isAsciiAuditReason(reason)) {
      setError('دلیل حسابرسی بارگذاری باید فقط به انگلیسی و با نویسه‌های ASCII نوشته شود؛ متن فارسی را به انگلیسی بازنویسی کنید.');
      return;
    }
    const extension = uploadFile.name.split('.').pop()?.toLowerCase();
    if (!['kml', 'kmz'].includes(extension)) {
      setError('فقط فایل KML یا KMZ انتخاب کنید.');
      return;
    }
    if (uploadFile.size > 25 * 1024 * 1024) {
      setError('حجم فایل باید کمتر از ۲۵ مگابایت باشد.');
      return;
    }
    setBusy('upload'); setError(''); setNotice(''); setUploadProgress(0);
    const result = await uploadManualCoverage({
      sourceId: uploadSourceId,
      file: uploadFile,
      requestId: requestId(),
      reason,
      onProgress: setUploadProgress,
    });
    setBusy('');
    if (!setResult(result, result.validation_scheduled
      ? 'فایل کامل دریافت شد؛ اعتبارسنجی سمت سرور ادامه دارد.'
      : 'فایل دریافت شد و نتیجه آماده است.')) return;
    setUploadProgress(100);
    setSelectedImportId(result.batch_id);
    setSelectedImport(null);
    setSelectedReport(null);
    setPollingImportId('');
    setUploadFile(null);
    if (fileInputRef.current) fileInputRef.current.value = '';
    await refreshAll({ quiet: true });
    if (result.validation_scheduled) setPollingImportId(result.batch_id);
    else await loadImport(result.batch_id);
  };

  const retireImport = async (entry, reason) => {
    setBusy(`import-retire-${entry.id}`);
    const result = await retireCoverageImport(entry.id, audit(reason));
    setBusy('');
    const retiredAreas = Number(result.import?.retired_areas);
    const success = Number.isFinite(retiredAreas)
      ? `بارگذاری بازنشسته شد و ${formatNumber(retiredAreas)} محدودهٔ پوشش آن از انتشار بعدی کنار گذاشته شد.`
      : 'بارگذاری بازنشسته شد و محدوده‌های پوشش آن از انتشار بعدی کنار گذاشته شدند.';
    if (!setResult(result, success)) return false;
    if (!result.import?.id) {
      setError('پاسخ بازنشستگی بارگذاری کامل نبود؛ صفحه را تازه‌سازی و نتیجه را بررسی کنید.');
      setNotice('');
      return false;
    }
    const retiredEntry = rememberRetired('imports', entry, result.import, setImports);
    if (selectedImportId === entry.id) {
      setSelectedImport((current) => (current ? {
        ...current,
        batch: { ...(current.batch || entry), ...retiredEntry },
      } : current));
      setSelectedReport((current) => (current ? { ...current, status: 'retired' } : current));
    }
    if (pollingImportId === entry.id) setPollingImportId('');
    return true;
  };

  const runConflictAction = async (conflict, actionName) => {
    const note = (conflictNotes[conflict.id] || '').trim();
    if (!note) { setError('برای اقدام روی تعارض، یادداشت تصمیم را وارد کنید.'); return; }
    setBusy(`conflict-${conflict.id}`);
    const actionFn = actionName === 'acknowledge'
      ? acknowledgeCoverageConflict : resolveCoverageConflict;
    const result = await actionFn(conflict.id, note, audit(note));
    setBusy('');
    if (!setResult(result, actionName === 'acknowledge' ? 'تعارض دیده‌شده ثبت شد.' : 'تعارض حل‌شده ثبت شد.')) return;
    setConflictNotes((current) => ({ ...current, [conflict.id]: '' }));
    setSelectedConflict(result.conflict || null);
    await refreshAll({ quiet: true });
  };

  const showConflict = async (id) => {
    const result = await getCoverageConflict(id);
    if (result.error) { setError(apiError(result)); return; }
    setSelectedConflict(result.conflict);
  };

  const submitPublishRequest = async (event) => {
    event.preventDefault();
    setBusy('publish-request');
    const result = await createCoveragePublishRequest({
      action: 'publish_scope',
      candidate_import_id: publishForm.import_id,
      candidate_policy_revision_id: publishForm.policy_id,
    }, audit(publishForm.reason));
    setBusy('');
    if (!setResult(result, 'درخواست انتشار ثبت شد و در انتظار تصمیم مدیر مجاز است.')) return;
    setPublishForm((current) => ({ ...current, reason: '' }));
    await refreshAll({ quiet: true });
  };

  const decidePublishRequest = async (entry, decision) => {
    const reason = (decisionReasons[entry.id] || '').trim();
    if (!reason) { setError('دلیل تأیید یا رد را وارد کنید.'); return; }
    setBusy(`publish-${entry.id}`);
    const result = decision === 'approve'
      ? await approveCoveragePublishRequest(entry.id, audit(reason))
      : await rejectCoveragePublishRequest(entry.id, audit(reason));
    setBusy('');
    if (!setResult(result, decision === 'approve' ? 'درخواست تأیید و نسخهٔ جدید منتشر شد.' : 'درخواست انتشار رد شد.')) return;
    setDecisionReasons((current) => ({ ...current, [entry.id]: '' }));
    await refreshAll({ quiet: true });
  };

  const showVersion = async (id) => {
    const result = await getCoverageVersion(id);
    if (result.error) { setError(apiError(result)); return; }
    setSelectedVersion(result.version);
  };

  const selectedImportEntry = imports.find((entry) => entry.id === selectedImportId) || null;
  const importReport = selectedReport?.report || selectedImport?.validation_report || null;
  const importBatch = selectedImport?.batch || null;
  const quarantine = importBatch?.quarantine || [];
  const reportErrors = importReport?.errors || [];
  const reportWarnings = importReport?.warnings || [];
  const importReasons = dedupeReasons([...reportErrors, ...quarantine]);
  const precedence = importReport?.precedence;
  const precedenceBlocked = precedence?.rule === 'operator_api_always_wins';

  if (visibleSteps.length === 0) {
    return <div className="cov-root iadmin" dir="rtl" role="status">شما به هیچ بخش پوشش دسترسی ندارید.</div>;
  }

  return (
    <section className="cov-root iadmin" dir="rtl">
      <header className="cov-head">
        <div>
          <div className="cov-kicker"><ShieldCheck size={15} /> کنترل شواهد پوشش</div>
          <h2>مدیریت پوشش اپراتورها</h2>
          <p>فایل اپراتور را دریافت کنید، نتیجهٔ پردازش فایل را ببینید، تعارض را تعیین تکلیف کنید و انتشار را به تأیید برسانید.</p>
        </div>
        <button type="button" className="cov-btn cov-btn-secondary" onClick={() => refreshAll()} disabled={loading}>
          <RefreshCw size={16} className={loading ? 'cov-spin' : ''} /> تازه‌سازی
        </button>
      </header>

      {error && <div className="cov-banner cov-banner-error"><AlertTriangle size={17} /> <span>{error}</span></div>}
      {notice && <div className="cov-banner cov-banner-success"><CheckCircle2 size={17} /> <span>{notice}</span></div>}

      <div className="cov-layout">
        <nav className="cov-steps" aria-label="مراحل مدیریت پوشش">
          {visibleSteps.map(({ id, label, icon: Icon }) => {
            const count = id === 'setup' ? acceptedSources.length
              : id === 'imports' ? imports.length
                : id === 'conflicts' ? conflicts.filter((item) => item.status !== 'resolved').length
                  : id === 'publication' ? publishRequests.filter((item) => item.status === 'pending').length
                    : versions.length;
            return (
              <button key={id} type="button" className={selectedStep === id ? 'is-active' : ''}
                onClick={() => setActiveStep(id)}>
                <Icon size={17} /> <span>{label}</span><b>{toFa(count)}</b>
              </button>
            );
          })}
        </nav>

        <div className="cov-workspace">
          {loading && <div className="cov-loading"><Loader2 className="cov-spin" /> در حال خواندن وضعیت پوشش…</div>}

          {!loading && selectedStep === 'setup' && (
            <div className="cov-screen">
              <div className="cov-screen-intro">
                <h3>راه‌اندازی محدوده و منبع</h3>
                <p>هر فایل باید به یک منبع دستی پذیرفته‌شده وصل باشد و هر منبع به محدودهٔ تجاری مشخصی تعلق دارد.</p>
              </div>
              {!canManageSetup && (
                <div className="cov-note">این بخش برای مدیر پوشش فقط خواندنی است.</div>
              )}
              {regions.length === 0 && sources.length === 0 && (
                <EmptyState icon={MapPinned}>پیش از اولین بارگذاری، محدودهٔ سرویس و یک منبع دستی پذیرفته‌شده بسازید.</EmptyState>
              )}
              <div className="cov-setup-grid">
                <section className="cov-panel">
                  <PanelTitle title="محدوده‌های پوشش" description="مرز تجاری معتبر با GeoJSON نوع MultiPolygon" count={regions.length} />
                  {regions.length > 0 && (
                    <div className="cov-compact-list">
                      {regions.map((region) => {
                        const retired = isRetired(region);
                        return (
                          <div className={`cov-compact-item${retired ? ' is-retired' : ''}`} key={region.id}>
                            <div className="cov-compact-row">
                              <div><strong>{region.display_name}</strong><span dir="ltr">{region.region_code}</span></div>
                              <div className="cov-inline-actions">
                                <StatusBadge status={retired ? 'retired' : region.status} />
                                {canManageSetup && !retired && (
                                  <button type="button" className="cov-row-menu" aria-expanded={regionActionId === region.id}
                                    onClick={() => openRegionActions(region)}>عملیات</button>
                                )}
                              </div>
                            </div>
                            {canManageSetup && !retired && regionActionId === region.id && (
                              <div className="cov-row-action-panel">
                                <form className="cov-row-edit-form" onSubmit={(event) => submitRegionEdit(event, region)}>
                                  <label><span>نام نمایشی</span><input required value={regionEditForm.display_name}
                                    onChange={(event) => setRegionEditForm((form) => ({ ...form, display_name: event.target.value }))} /></label>
                                  <label className="cov-field-wide"><span>هندسهٔ GeoJSON اصلاح‌شده (اختیاری)</span><textarea rows={4}
                                    value={regionEditForm.geometry}
                                    onChange={(event) => setRegionEditForm((form) => ({ ...form, geometry: event.target.value }))}
                                    placeholder="اگر مرز نیاز به اصلاح ندارد، این فیلد را خالی بگذارید."
                                    dir="ltr" /></label>
                                  <label className="cov-field-wide"><span>دلیل ثبت در حسابرسی</span><input required value={regionEditForm.reason}
                                    onChange={(event) => setRegionEditForm((form) => ({ ...form, reason: event.target.value }))}
                                    placeholder="مثلاً اصلاح نام یا مرز بر پایهٔ سند اپراتور" /></label>
                                  <button className="cov-btn cov-btn-primary cov-field-wide"
                                    disabled={busy === `region-edit-${region.id}` || !regionEditForm.display_name.trim() || !regionEditForm.reason.trim()}>
                                    ثبت اصلاح محدوده
                                  </button>
                                </form>
                                <RetirementAction
                                  itemLabel={region.display_name}
                                  busy={busy === `region-retire-${region.id}`}
                                  onConfirm={(reason) => retireRegion(region, reason)}
                                />
                              </div>
                            )}
                          </div>
                        );
                      })}
                    </div>
                  )}
                  {canManageSetup && (
                    <form className="cov-form" onSubmit={submitRegion}>
                    <label><span>کد محدوده</span><input required value={regionForm.region_code}
                      onChange={(e) => setRegionForm((form) => ({ ...form, region_code: e.target.value }))}
                      placeholder="مثلاً THR-01" dir="ltr" /></label>
                    <label><span>نام نمایشی</span><input required value={regionForm.display_name}
                      onChange={(e) => setRegionForm((form) => ({ ...form, display_name: e.target.value }))}
                      placeholder="مثلاً تهران - منطقهٔ یک" /></label>
                    <label className="cov-field-wide"><span>هندسهٔ GeoJSON</span><textarea required rows={5}
                      value={regionForm.geometry}
                      onChange={(e) => setRegionForm((form) => ({ ...form, geometry: e.target.value }))}
                      placeholder='{"type":"MultiPolygon","coordinates":[...]}' dir="ltr" /></label>
                    <label className="cov-field-wide"><span>دلیل ثبت در حسابرسی</span><input required value={regionForm.reason}
                      onChange={(e) => setRegionForm((form) => ({ ...form, reason: e.target.value }))}
                      placeholder="مثلاً دریافت محدودهٔ رسمی از اپراتور" /></label>
                      <button className="cov-btn cov-btn-primary cov-field-wide" disabled={busy === 'region'}>
                        {busy === 'region' ? <Loader2 className="cov-spin" size={16} /> : <Plus size={16} />} ساخت محدوده
                      </button>
                    </form>
                  )}
                </section>

                <section className="cov-panel">
                  <PanelTitle title="منابع دستی" description="منبع پذیرفته‌شده برای فایل‌های KML / KMZ" count={sources.length} />
                  {!canManageCatalogue && (
                    <div className="cov-note cov-registry-permission-note">
                      اگر سرویس موردنظر در رجیستری سرویس پوشش نیست، یک مدیر کاتالوگ باید آن را اضافه کند؛ افزودن سرویس در «فروشگاه» این رجیستری را تغییر نمی‌دهد.
                    </div>
                  )}
                  {sources.length > 0 && (
                    <div className="cov-compact-list">
                      {sources.map((source) => {
                        const retired = isRetired(source);
                        return (
                          <div className={`cov-compact-item${retired ? ' is-retired' : ''}`} key={source.id}>
                            <div className="cov-compact-row">
                              <div><strong>{source.source_name}</strong><span>{source.carrier_name || source.carrier_slug} · {source.region_name}</span></div>
                              <div className="cov-inline-actions">
                                <StatusBadge status={retired ? 'retired' : source.acceptance_status} />
                                {canManageSetup && source.acceptance_status !== 'accepted' && !retired && (
                                  <button type="button" className="cov-text-btn" disabled={busy === `source-${source.id}`}
                                    onClick={() => acceptSource(source)}>پذیرش</button>
                                )}
                                {canManageSetup && !retired && (
                                  <button type="button" className="cov-row-menu" aria-expanded={sourceActionId === source.id}
                                    onClick={() => setSourceActionId((current) => (current === source.id ? '' : source.id))}>عملیات</button>
                                )}
                              </div>
                            </div>
                            {canManageSetup && !retired && sourceActionId === source.id && (
                              <div className="cov-row-action-panel">
                                <RetirementAction
                                  itemLabel={source.source_name}
                                  busy={busy === `source-retire-${source.id}`}
                                  onConfirm={(reason) => retireSource(source, reason)}
                                />
                              </div>
                            )}
                          </div>
                        );
                      })}
                    </div>
                  )}
                  {canManageSetup && (
                    <form className="cov-form" onSubmit={submitSource}>
                    <EntityPicker
                      label="اپراتور"
                      value={sourceForm.carrier_id}
                      onChange={(carrier) => {
                        setSourceServiceCount(null);
                        setSourceServices([]);
                        setServiceActionId('');
                        setShowServiceForm(false);
                        setServiceForm(EMPTY_SERVICE);
                        setServiceFormError('');
                        setSourceForm((form) => ({
                          ...form,
                          carrier_id: carrier?.id || '',
                          service_type: '',
                        }));
                      }}
                      loadOptions={listIntakeCarriers}
                      emptyMessage="اپراتور فعالی وجود ندارد. ابتدا اپراتور را در تب «اپراتورها» ثبت کنید."
                    />
                    <EntityPicker
                      label="نوع سرویس"
                      value={sourceForm.service_type}
                      onChange={(service) => setSourceForm((form) => ({
                        ...form,
                        service_type: service?.id || '',
                      }))}
                      loadOptions={loadSourceServices}
                      deps={[sourceForm.carrier_id, serviceReloadKey]}
                      disabled={!sourceForm.carrier_id.trim()}
                      placeholder={sourceForm.carrier_id.trim() ? 'جست‌وجو و انتخاب سرویس' : 'ابتدا اپراتور را انتخاب کنید'}
                      getOptionLabel={(service) => service.display_name || service.service_type}
                      emptyMessage="این اپراتور در رجیستری سرویس پوشش، سرویس فعالی ندارد. از فرم زیر سرویس را اضافه کنید."
                    />
                    {sourceForm.carrier_id && (
                      <div className="cov-registry-help cov-field-wide">
                        <p>این انتخاب‌گر رجیستری سرویس پوشش را می‌خواند و افزودن سرویس در «فروشگاه» چیزی به این رجیستری اضافه نمی‌کند.</p>
                        {sourceServiceCount === 0 && (
                          <div className="cov-note cov-note-warning">
                            این اپراتور در رجیستری سرویس پوشش هیچ سرویس فعالی ندارد.
                          </div>
                        )}
                        {canManageCatalogue ? (
                          <>
                            <button type="button" className="cov-text-btn cov-registry-add"
                              aria-expanded={showServiceForm}
                              onClick={() => { setShowServiceForm((current) => !current); setServiceFormError(''); }}>
                              افزودن سرویس به رجیستری پوشش
                            </button>
                            {showServiceForm && (
                              <div className="cov-registry-form" role="group" aria-label="افزودن سرویس به رجیستری پوشش">
                                <label><span>نام نمایشی</span><input value={serviceForm.display_name}
                                  onChange={(event) => setServiceForm((form) => ({ ...form, display_name: event.target.value }))}
                                  placeholder="مثلاً فیبر نوری مبین‌نت" /></label>
                                <label><span>کد نوع سرویس</span><input value={serviceForm.service_type}
                                  onChange={(event) => { setServiceForm((form) => ({ ...form, service_type: event.target.value })); setServiceFormError(''); }}
                                  list="coverage-service-type-codes" dir="ltr" placeholder="fiber" aria-describedby="cov-service-type-hint" />
                                  <datalist id="coverage-service-type-codes">
                                    {[...new Set([...SERVICE_TYPE_SUGGESTIONS, ...sourceServices.map((service) => service.service_type).filter(Boolean)])]
                                      .map((code) => <option key={code} value={code} />)}
                                  </datalist>
                                  <small id="cov-service-type-hint" className="cov-field-hint">کد با حرف کوچک انگلیسی آغاز می‌شود و می‌تواند عدد، خط تیره یا زیرخط داشته باشد؛ مانند fiber یا fixed_fiber.</small>
                                </label>
                                <label className="cov-field-wide"><span>دلیل ثبت در حسابرسی</span><input value={serviceForm.reason}
                                  onChange={(event) => setServiceForm((form) => ({ ...form, reason: event.target.value }))}
                                  placeholder="مثلاً افزودن سرویس رسمی اپراتور برای ورود KMZ" /></label>
                                {serviceFormError && <div className="cov-inline-error cov-field-wide" role="alert">{serviceFormError}</div>}
                                <button type="button" className="cov-btn cov-btn-primary cov-field-wide"
                                  disabled={busy === 'service-create' || !serviceForm.display_name.trim() || !serviceForm.service_type.trim() || !serviceForm.reason.trim()}
                                  onClick={submitRegistryService}>
                                  {busy === 'service-create' ? <Loader2 className="cov-spin" size={16} /> : <Plus size={16} />}
                                  افزودن و انتخاب سرویس
                                </button>
                              </div>
                            )}
                          </>
                        ) : (
                          <div className="cov-note">
                            اگر سرویس موردنظر در رجیستری سرویس پوشش نیست، یک مدیر کاتالوگ باید آن را اضافه کند؛ ثبت سرویس در «فروشگاه» این رجیستری را کامل نمی‌کند.
                          </div>
                        )}
                        {sourceServices.length > 0 && (
                          <details className="cov-registry-list">
                            <summary>سرویس‌های رجیستری پوشش <span>{toFa(sourceServices.length)}</span></summary>
                            <div>
                              {sourceServices.map((service) => {
                                const retired = isRetired(service);
                                return (
                                  <div className={`cov-registry-row${retired ? ' is-retired' : ''}`} key={service.id}>
                                    <div><strong>{service.display_name}</strong><code dir="ltr">{service.service_type}</code></div>
                                    <div className="cov-inline-actions">
                                      <StatusBadge status={retired ? 'retired' : service.status} />
                                      {canManageCatalogue && !retired && (
                                        <button type="button" className="cov-row-menu" aria-expanded={serviceActionId === service.id}
                                          onClick={() => setServiceActionId((current) => (current === service.id ? '' : service.id))}>عملیات</button>
                                      )}
                                    </div>
                                    {canManageCatalogue && !retired && serviceActionId === service.id && (
                                      <div className="cov-row-action-panel cov-registry-row-actions">
                                        <RetirementAction
                                          itemLabel={service.display_name}
                                          busy={busy === `service-retire-${service.id}`}
                                          onConfirm={(reason) => retireRegistryService(service, reason)}
                                        />
                                      </div>
                                    )}
                                  </div>
                                );
                              })}
                            </div>
                          </details>
                        )}
                      </div>
                    )}
                    <label><span>محدوده</span><select required value={sourceForm.region_id}
                      onChange={(e) => setSourceForm((form) => ({ ...form, region_id: e.target.value }))}
                      disabled={!activeRegions.length}>
                      <option value="">انتخاب محدوده</option>
                      {activeRegions.map((region) => <option key={region.id} value={region.id}>{region.display_name}</option>)}
                    </select></label>
                    <label><span>نام منبع</span><input required value={sourceForm.source_name}
                      onChange={(e) => setSourceForm((form) => ({ ...form, source_name: e.target.value }))}
                      placeholder="مثلاً فایل رسمی اپراتور - مرداد ۱۴۰۵" /></label>
                    <label className="cov-field-wide"><span>دلیل ثبت یا پذیرش</span><input required value={sourceForm.reason}
                      onChange={(e) => setSourceForm((form) => ({ ...form, reason: e.target.value }))}
                      placeholder="مثلاً منبع رسمی تحویل‌شده توسط اپراتور" /></label>
                      <button className="cov-btn cov-btn-primary cov-field-wide"
                        disabled={busy === 'source' || !activeRegions.length || !sourceForm.service_type}>
                        {busy === 'source' ? <Loader2 className="cov-spin" size={16} /> : <Plus size={16} />} ساخت و پذیرش منبع
                      </button>
                    </form>
                  )}
                </section>
              </div>
            </div>
          )}

          {!loading && selectedStep === 'imports' && (
            <div className="cov-screen">
              <div className="cov-screen-intro">
                <h3>بارگذاری فایل و گزارش اعتبارسنجی</h3>
                <p>پیشرفت تا رسیدن کامل بایت‌ها به سرور نمایش داده می‌شود؛ پس از آن، وضعیت پردازش فایل جداگانه پایش می‌شود.</p>
              </div>
              {canManageSetup && <GeometryIntake />}
              <section className="cov-panel cov-upload-panel">
                <form className="cov-upload-form" onSubmit={submitUpload}>
                  <label><span>منبع پذیرفته‌شده</span><select required value={uploadSourceId}
                    onChange={(e) => setUploadSourceId(e.target.value)} disabled={!acceptedSources.length || busy === 'upload'}>
                    <option value="">انتخاب منبع</option>
                    {acceptedSources.map((source) => (
                      <option key={source.id} value={source.id}>{source.source_name} · {source.region_name}</option>
                    ))}
                  </select></label>
                  <label className="cov-upload-file">
                    <input ref={fileInputRef} type="file" accept=".kml,.kmz,application/vnd.google-earth.kml+xml,application/vnd.google-earth.kmz"
                      onChange={(e) => setUploadFile(e.target.files?.[0] || null)} disabled={!acceptedSources.length || busy === 'upload'} />
                    <FileArchive size={28} />
                    <strong>{uploadFile ? uploadFile.name : 'فایل KML یا KMZ را انتخاب کنید'}</strong>
                    <span>{uploadFile ? formatBytes(uploadFile.size) : 'حداکثر ۲۵ مگابایت؛ فایل‌های بزرگ به‌صورت streaming ارسال می‌شوند.'}</span>
                  </label>
                  <label><span>دلیل بارگذاری در حسابرسی (فقط انگلیسی / ASCII)</span><input required value={uploadReason}
                    onChange={(e) => setUploadReason(e.target.value)} disabled={busy === 'upload'}
                    placeholder="e.g. official operator coverage received 2026-08-05" dir="ltr"
                    aria-describedby="cov-upload-audit-hint" />
                    <small id="cov-upload-audit-hint" className="cov-field-hint">این فیلد برای هدر امن بارگذاری باید فقط با حروف و نویسه‌های ASCII انگلیسی نوشته شود.</small>
                  </label>
                  <button className="cov-btn cov-btn-primary" disabled={!uploadFile || !uploadSourceId || !uploadReason.trim() || busy === 'upload'}>
                    {busy === 'upload' ? <Loader2 className="cov-spin" size={16} /> : <UploadCloud size={16} />}
                    {busy === 'upload' ? `ارسال ${toFa(uploadProgress)}٪` : 'شروع بارگذاری'}
                  </button>
                </form>
                {(busy === 'upload' || uploadProgress > 0 || pollingImportId) && (
                  <div className="cov-progress" aria-live="polite">
                    <div className="cov-progress-track"><span style={{ transform: `scaleX(${uploadProgress / 100})` }} /></div>
                    <div>
                      <strong>{busy === 'upload' ? `دریافت فایل در سرور: ${toFa(uploadProgress)}٪` : 'فایل کامل دریافت شد.'}</strong>
                      <span>{pollingImportId ? 'اعتبارسنجی مختصات، آرشیو و هندسه در حال اجراست…' : 'نتیجهٔ نهایی در گزارش پایین نمایش داده می‌شود.'}</span>
                    </div>
                  </div>
                )}
                {!acceptedSources.length && (
                  <div className="cov-note cov-note-warning">برای بارگذاری، ابتدا در مرحلهٔ قبل یک منبع دستی با وضعیت «پذیرفته» بسازید.</div>
                )}
              </section>

              {imports.length === 0 ? (
                <EmptyState icon={UploadCloud}>هنوز فایل پوششی دریافت نشده است. پس از ساخت منبع، فایل KML یا KMZ اپراتور را از همین صفحه بارگذاری کنید.</EmptyState>
              ) : (
                <div className="cov-report-layout">
                  <section className="cov-panel cov-import-list">
                    <PanelTitle title="بارگذاری‌ها" count={imports.length} />
                    {imports.map((entry) => (
                      <button type="button" key={entry.id} className={selectedImportId === entry.id ? 'is-selected' : ''}
                        onClick={() => loadImport(entry.id)}>
                        <FileArchive size={17} />
                        <span><strong>{entry.original_filename}</strong><small>{entry.source_name} · {formatDate(entry.received_at)}</small></span>
                        <StatusBadge status={isRetired(entry) ? 'retired' : entry.status} />
                      </button>
                    ))}
                  </section>

                  <section className="cov-panel cov-report">
                    <PanelTitle title="گزارش پردازش فایل" description={importBatch?.original_filename || 'یک بارگذاری را انتخاب کنید'} />
                    {canManageSetup && selectedImportEntry && !isRetired(selectedImportEntry) && (
                      <div className="cov-import-retirement">
                        <RetirementAction
                          itemLabel={selectedImportEntry.original_filename}
                          busy={busy === `import-retire-${selectedImportEntry.id}`}
                          onConfirm={(reason) => retireImport(selectedImportEntry, reason)}
                        />
                      </div>
                    )}
                    {!importReport && <div className="cov-loading"><Loader2 className="cov-spin" /> در حال خواندن گزارش…</div>}
                    {importReport && (
                      <>
                        <div className="cov-report-summary">
                          <div><span>مرحله‌بندی</span><strong>{toFa(importReport.counts?.staged || 0)}</strong></div>
                          <div className="is-success"><span>پذیرفته</span><strong>{toFa(importReport.counts?.accepted || 0)}</strong></div>
                          <div className="is-warning"><span>قرنطینه</span><strong>{toFa(importReport.counts?.quarantined || 0)}</strong></div>
                          <div><span>مختصات</span><strong>{toFa(importReport.counts?.coordinates || 0)}</strong></div>
                        </div>
                        <dl className="cov-report-meta">
                          <div><dt>وضعیت</dt><dd><StatusBadge status={selectedReport?.status || importBatch?.status} /></dd></div>
                          <div><dt>SHA-256</dt><dd className="cov-code">{selectedReport?.content_sha256 || importReport.file?.sha256 || '-'}</dd></div>
                          <div><dt>حجم فشرده</dt><dd>{formatBytes(importReport.file?.compressed_bytes)}</dd></div>
                          <div><dt>مساحت پذیرفته</dt><dd>{formatNumber(importReport.area_m2?.accepted_sum, { maximumFractionDigits: 0 })} مترمربع</dd></div>
                        </dl>
                        {precedenceBlocked && (
                          <div className="cov-note cov-note-warning">
                            <strong>این بارگذاری قابل انتشار نیست؛ طبق قاعده «API اپراتور همیشه برنده است» منبع API برنده شده است.</strong>
                            {precedence.blocking_api_source_id && <span>شناسهٔ منبع API مسدودکننده: {precedence.blocking_api_source_id}</span>}
                            {precedence.winner_import_id && <span>شناسهٔ بارگذاری برنده: {precedence.winner_import_id}</span>}
                            {precedence.api_health_status && <span>وضعیت سلامت API: {precedence.api_health_status}</span>}
                          </div>
                        )}
                        {!precedenceBlocked && importReasons.length === 0 && (
                          <div className="cov-clean-report"><CheckCircle2 size={19} /> هیچ خطای قرنطینه‌ای ثبت نشده است.</div>
                        )}
                        {importReasons.length > 0 && (
                          <div className="cov-reasons">
                            <h4><FileWarning size={17} /> دلایل قرنطینه</h4>
                            {importReasons.map((item, index) => (
                              <article key={`${item.code || item.reason_code}-${index}`}>
                                <code>{item.code || item.reason_code}</code>
                                <p>{coverageReasonFa(item)}</p>
                                {(item.feature_ordinal || item.external_feature_id) && (
                                  <span>عارضه {toFa(item.feature_ordinal || '-')} · {item.external_feature_id || 'بدون شناسه'}</span>
                                )}
                                {(item.evidence || item.reason_detail) && (
                                  <pre>{item.reason_detail || JSON.stringify(item.evidence, null, 2)}</pre>
                                )}
                              </article>
                            ))}
                          </div>
                        )}
                        {reportWarnings.length > 0 && (
                          <details className="cov-warnings">
                            <summary>{toFa(reportWarnings.length)} هشدار غیرمسدودکننده</summary>
                            {reportWarnings.map((item, index) => (
                              <p key={`${item.code}-${index}`}><code>{item.code}</code> {coverageReasonFa(item)}</p>
                            ))}
                          </details>
                        )}
                      </>
                    )}
                  </section>
                </div>
              )}
            </div>
          )}

          {!loading && selectedStep === 'conflicts' && (
            <div className="cov-screen">
              <div className="cov-screen-intro">
                <h3>تعارض منبع دستی و API</h3>
                <p>منبع API پذیرفته‌شده همیشه برنده است. اینجا اختلاف را می‌بینید، ثبت می‌کنید و با یادداشت حل می‌کنید.</p>
              </div>
              {conflicts.length === 0 ? (
                <EmptyState icon={AlertTriangle}>تا زمانی که فایل دستی با منبع API پذیرفته‌شده هم‌پوشانی نداشته باشد، تعارضی اینجا ظاهر نمی‌شود.</EmptyState>
              ) : conflicts.map((conflict) => (
                <section className="cov-panel cov-conflict" key={conflict.id}>
                  <div className="cov-row-head">
                    <div><strong>{conflict.carrier_slug} · {conflict.service_type}</strong><span>محدوده {conflict.region_key} · {formatDate(conflict.detected_at)}</span></div>
                    <StatusBadge status={conflict.status} />
                  </div>
                  <div className="cov-diff-strip">
                    <span>API: {toFa(conflict.api_features || 0)} عارضه</span>
                    <span>دستی: {toFa(conflict.manual_features || 0)} عارضه</span>
                    <b>قاعده: API همیشه برنده است</b>
                  </div>
                  <label className="cov-inline-field"><span>یادداشت تصمیم</span><input
                    value={conflictNotes[conflict.id] || ''}
                    onChange={(e) => setConflictNotes((current) => ({ ...current, [conflict.id]: e.target.value }))}
                    placeholder="آنچه بررسی شد و تصمیم مدیر" /></label>
                  <div className="cov-inline-actions">
                    <button type="button" className="cov-btn cov-btn-secondary" onClick={() => showConflict(conflict.id)}>جزئیات اختلاف</button>
                    {conflict.status === 'open' && (
                      <button type="button" className="cov-btn cov-btn-warning" disabled={busy === `conflict-${conflict.id}`}
                        onClick={() => runConflictAction(conflict, 'acknowledge')}>ثبت مشاهده</button>
                    )}
                    {conflict.status !== 'resolved' && (
                      <button type="button" className="cov-btn cov-btn-primary" disabled={busy === `conflict-${conflict.id}`}
                        onClick={() => runConflictAction(conflict, 'resolve')}>حل با یادداشت</button>
                    )}
                  </div>
                  {selectedConflict?.id === conflict.id && (
                    <div className="cov-conflict-detail">
                      <div><span>هم‌پوشانی</span><strong>{formatNumber(selectedConflict.area_m2?.overlap, { maximumFractionDigits: 0 })} مترمربع</strong></div>
                      <div><span>فقط API</span><strong>{formatNumber(selectedConflict.area_m2?.api_only, { maximumFractionDigits: 0 })} مترمربع</strong></div>
                      <div><span>فقط دستی</span><strong>{formatNumber(selectedConflict.area_m2?.manual_only, { maximumFractionDigits: 0 })} مترمربع</strong></div>
                      <div><span>تغییر مساحت</span><strong>{formatNumber(selectedConflict.change?.covered_area_percent, { maximumFractionDigits: 1 })}٪</strong></div>
                    </div>
                  )}
                </section>
              ))}
            </div>
          )}

          {!loading && selectedStep === 'publication' && (
            <div className="cov-screen">
              <div className="cov-screen-intro">
                <h3>درخواست و تصمیم انتشار</h3>
                <p>فایل آماده را با یک خط‌مشی فعال پیشنهاد کنید. انتشار فقط پس از تأیید جداگانه اجرا می‌شود.</p>
              </div>
              {!canPublish && (
                <div className="cov-note">این بخش برای مدیر پوشش فقط خواندنی است.</div>
              )}
              {canPublish && (
                <section className="cov-panel">
                  <PanelTitle title="درخواست انتشار جدید" description="فقط بارگذاری آماده و خط‌مشی فعال قابل انتخاب است" />
                  <form className="cov-form cov-publish-form" onSubmit={submitPublishRequest}>
                  <label><span>بارگذاری نامزد</span><select required value={publishForm.import_id}
                    onChange={(e) => setPublishForm((form) => ({ ...form, import_id: e.target.value }))}>
                    <option value="">انتخاب فایل آماده</option>
                    {readyImports.map((entry) => <option key={entry.id} value={entry.id}>{entry.original_filename} · {entry.source_name}</option>)}
                  </select></label>
                  <label><span>خط‌مشی فعال</span><select required value={publishForm.policy_id}
                    onChange={(e) => setPublishForm((form) => ({ ...form, policy_id: e.target.value }))}>
                    <option value="">انتخاب خط‌مشی</option>
                    {activePolicies.map((policy) => <option key={policy.id} value={policy.id}>{policy.policy_key} · بازبینی {toFa(policy.revision)}</option>)}
                  </select></label>
                  <label className="cov-field-wide"><span>دلیل درخواست</span><input required value={publishForm.reason}
                    onChange={(e) => setPublishForm((form) => ({ ...form, reason: e.target.value }))}
                    placeholder="چرا این فایل باید نسخهٔ جاری شود؟" /></label>
                  {!activePolicies.length && (
                    <div className="cov-note cov-note-warning cov-field-wide">
                      خط‌مشی فعالی وجود ندارد. API فعلی پیش‌نویس خط‌مشی می‌سازد اما endpoint فعال‌سازی خط‌مشی ندارد؛ تا رفع این شکاف، درخواست انتشار از صفر قابل تکمیل نیست.
                    </div>
                  )}
                  <button className="cov-btn cov-btn-primary cov-field-wide"
                    disabled={busy === 'publish-request' || !publishForm.import_id || !publishForm.policy_id || !publishForm.reason.trim()}>
                    <GitPullRequest size={16} /> ثبت درخواست انتشار
                  </button>
                  </form>
                </section>
              )}

              {publishRequests.length === 0 ? (
                <EmptyState icon={GitPullRequest}>پس از آماده شدن یک بارگذاری و انتخاب خط‌مشی فعال، درخواست انتشار را ثبت کنید.</EmptyState>
              ) : publishRequests.map((entry) => (
                <section className="cov-panel cov-publish-row" key={entry.id}>
                  <div className="cov-row-head">
                    <div><strong>درخواست {statusFa(entry.action)}</strong><span>{formatDate(entry.requested_at)} · {entry.candidate_content_sha256 || entry.candidate_import_id || entry.target_version_id}</span></div>
                    <StatusBadge status={entry.status} />
                  </div>
                  {canPublish && entry.status === 'pending' && (
                    <>
                      <label className="cov-inline-field"><span>دلیل تصمیم</span><input
                        value={decisionReasons[entry.id] || ''}
                        onChange={(e) => setDecisionReasons((current) => ({ ...current, [entry.id]: e.target.value }))}
                        placeholder="مبنای تأیید یا رد" /></label>
                      <div className="cov-inline-actions">
                        <button type="button" className="cov-btn cov-btn-primary" disabled={busy === `publish-${entry.id}`}
                          onClick={() => decidePublishRequest(entry, 'approve')}><CheckCircle2 size={16} /> تأیید و انتشار</button>
                        <button type="button" className="cov-btn cov-btn-danger" disabled={busy === `publish-${entry.id}`}
                          onClick={() => decidePublishRequest(entry, 'reject')}><XCircle size={16} /> رد درخواست</button>
                      </div>
                    </>
                  )}
                </section>
              ))}
            </div>
          )}

          {!loading && selectedStep === 'versions' && (
            <div className="cov-screen">
              <div className="cov-screen-intro">
                <h3>نسخه‌های سرویس‌پذیری</h3>
                <p>نسخهٔ جاری با نشان زنده مشخص است. جزئیات هر نسخه، محدوده‌ها و منبع انتخاب‌شده را نمایش می‌دهد.</p>
              </div>
              {versions.length === 0 ? (
                <EmptyState icon={Layers3}>هنوز نسخه‌ای منتشر نشده است. نخستین نسخه پس از تأیید درخواست انتشار ساخته می‌شود.</EmptyState>
              ) : (
                <div className="cov-version-layout">
                  <section className="cov-panel cov-version-list">
                    {versions.map((version) => (
                      <button type="button" key={version.id}
                        className={selectedVersion?.id === version.id ? 'is-selected' : ''}
                        onClick={() => showVersion(version.id)}>
                        <span><strong>نسخهٔ {toFa(version.version_no)}</strong><small>{formatDate(version.published_at || version.created_at)}</small></span>
                        <StatusBadge status={version.status} current={version.is_current} />
                        <span className="cov-version-stats">{toFa(version.scope_count || 0)} محدوده · {toFa(version.zone_count || 0)} ناحیه</span>
                      </button>
                    ))}
                  </section>
                  <section className="cov-panel cov-version-detail">
                    {!selectedVersion ? (
                      <EmptyState icon={Layers3}>برای دیدن محدوده‌ها و منابع، یک نسخه را انتخاب کنید.</EmptyState>
                    ) : (
                      <>
                        <PanelTitle title={`نسخهٔ ${toFa(selectedVersion.version_no)}`} description={selectedVersion.publication_notes} />
                        <dl className="cov-report-meta">
                          <div><dt>وضعیت</dt><dd><StatusBadge status={selectedVersion.status} current={selectedVersion.is_current} /></dd></div>
                          <div><dt>SHA-256</dt><dd className="cov-code">{selectedVersion.content_sha256}</dd></div>
                          <div><dt>انتشار</dt><dd>{formatDate(selectedVersion.published_at)}</dd></div>
                          <div><dt>خط‌مشی</dt><dd className="cov-code">{selectedVersion.policy_revision_id}</dd></div>
                        </dl>
                        {(selectedVersion.scopes || []).length === 0 ? (
                          <EmptyState icon={Layers3}>این نسخه هیچ محدوده‌ای ندارد. درخواست انتشار مبدأ را بررسی کنید یا نسخهٔ دیگری را انتخاب کنید.</EmptyState>
                        ) : (
                          <div className="cov-table-wrap">
                            <table className="cov-table">
                              <thead><tr><th>اپراتور</th><th>سرویس</th><th>محدوده</th><th>منبع</th><th>فایل</th></tr></thead>
                              <tbody>
                                {selectedVersion.scopes.map((scope) => (
                                  <tr key={scope.id}><td>{scope.carrier_slug}</td><td>{scope.service_type}</td><td>{scope.region_code}</td><td>{scope.source_name}</td><td>{scope.original_filename}</td></tr>
                                ))}
                              </tbody>
                            </table>
                          </div>
                        )}
                      </>
                    )}
                  </section>
                </div>
              )}
            </div>
          )}
        </div>
      </div>
    </section>
  );
}
